import { FC } from 'react'

import { SequenceMenu, SetIsOpen } from '@sequencehq/core-components'
import { useAtomValue, useSetAtom } from 'jotai'
import { useNavigate } from 'react-router-dom'

import { usePermissions } from 'lib/rbac'
import {
  editingVersionAtom,
  activeVersionAtom
} from 'Automations/automationEditor/atoms'
import { useAutomationId } from 'Automations/automationEditor/hooks/useAutomationId/useAutomationId'
import { PublishWorkflowMenuItem } from 'Automations/automationsManagement/components/WorkflowCard/PublishWorkflow.menuItem'
import { UnpublishWorkflowMenuItem } from 'Automations/automationsManagement/components/WorkflowCard/UnpublishWorkflow.menuItem'
import { ArchiveWorkflowMenuItem } from 'Automations/automationsManagement/components/WorkflowCard/ArchiveWorkflow.menuItem'
import { IGNORE_BLOCKER_STATE } from 'common/components/Blocker'

import { VersionHistoryMenuItem } from './VersionHistory.menuItem'
import { DiscardUnpublishedChangesMenuItem } from './DiscardUnpublishedChanges.menuItem'

export const KebabMenu: FC = () => {
  const { automationId, isNewAutomation } = useAutomationId()
  const editingVersion = useAtomValue(editingVersionAtom)
  const activeVersion = useAtomValue(activeVersionAtom)
  const setEditingVersion = useSetAtom(editingVersionAtom)
  const setActiveVersion = useSetAtom(activeVersionAtom)
  const permissions = usePermissions()
  const navigate = useNavigate()

  const canArchive = permissions.can('org.watchtower:archive')
  const canUpdate = permissions.can('org.watchtower:update')
  const canCreate = permissions.can('org.watchtower:create')
  const showWorkflowActions = automationId && !isNewAutomation && editingVersion

  const items = [
    ...(canCreate || canUpdate
      ? [
          ({ setIsOpen }: { setIsOpen?: SetIsOpen }) => (
            <VersionHistoryMenuItem
              key="version-history"
              onClick={() => setIsOpen?.(false)}
            />
          )
        ]
      : []),
    ...(showWorkflowActions
      ? [
          ...(canUpdate && editingVersion.active
            ? [
                ({ setIsOpen }: { setIsOpen?: SetIsOpen }) => (
                  <UnpublishWorkflowMenuItem
                    key="unpublish-workflow"
                    automationId={automationId}
                    versionId={editingVersion.id}
                    onSuccess={() => {
                      setEditingVersion({
                        ...editingVersion,
                        active: false
                      })
                      setActiveVersion(undefined)
                      setIsOpen?.(false)
                    }}
                  />
                )
              ]
            : []),
          ...(canUpdate && !editingVersion.active
            ? [
                ({ setIsOpen }: { setIsOpen?: SetIsOpen }) => (
                  <PublishWorkflowMenuItem
                    key="publish-workflow"
                    automationId={automationId}
                    versionId={editingVersion.id}
                    triggerType={editingVersion.triggerType}
                    onSuccess={() => {
                      const updatedVersion = {
                        ...editingVersion,
                        active: true
                      }
                      setEditingVersion(updatedVersion)
                      setActiveVersion(updatedVersion)
                      setIsOpen?.(false)
                    }}
                  />
                )
              ]
            : []),
          ...(activeVersion &&
          editingVersion.id !== activeVersion.id &&
          canArchive
            ? [
                ({ setIsOpen }: { setIsOpen?: SetIsOpen }) => (
                  <DiscardUnpublishedChangesMenuItem
                    key="discard-unpublished-changes"
                    automationId={automationId}
                    activeVersion={activeVersion}
                    onSuccess={() => setIsOpen?.(false)}
                  />
                )
              ]
            : []),
          ...(canArchive
            ? [
                ({ setIsOpen }: { setIsOpen?: SetIsOpen }) => (
                  <ArchiveWorkflowMenuItem
                    key="archive-workflow"
                    automationId={automationId}
                    versionId={editingVersion.id}
                    onSuccess={() => {
                      setIsOpen?.(false)
                      // The workflow no longer exists, so leaving must not
                      // trigger the unsaved-changes confirmation.
                      navigate('/workflows/management', {
                        state: IGNORE_BLOCKER_STATE
                      })
                    }}
                  />
                )
              ]
            : [])
        ]
      : [])
  ]

  if (items.length === 0) return null

  return (
    <SequenceMenu
      data-testid="automations.editor.kebabMenu"
      variant="ghost"
      alignment="bottom-right"
      items={items}
    />
  )
}
